import { useState } from "react";
import { toast } from "sonner";
import {
 Container, Section, PageHero, MapEmbed, CtaBanner, Phone, MapPin, Clock, ArrowRight,
 PHONE, PHONE_RAW,
} from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitAppointment } from "../lib/api";
import { useSeo, breadcrumbLd, ORIGIN } from "../lib/seo";
import { Mail } from "lucide-react";

export default function Contact() {
 const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
 const [sending, setSending] = useState(false);
 useSeo({
 title: "Contact RPM Auto Repair | Riverside, CA Auto Mechanic",
 description: "Contact RPM Auto Repair, trusted ASE-certified mechanics in Riverside, CA. Call (951) 653-4777, email, or message us. Located right off the 215 freeway on E Alessandro Blvd.",
 path: "/contact",
 jsonLd: [
 { "@context": "https://schema.org", "@type": "ContactPage", name: "Contact RPM Auto Repair", url: `${ORIGIN}/contact` },
 breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
 ],
 });
 const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
 const onSubmit = async (e) => {
 e.preventDefault();
 if (!form.name || !form.phone || !form.email) { toast.error("Please fill in name, phone and email."); return; }
 setSending(true);
 try {
 await submitAppointment({ ...form, service: "General Inquiry" });
 toast.success("Message sent! We'll get back to you soon.");
 setForm({ name: "", phone: "", email: "", message: "" });
 } catch { toast.error("Something went wrong. Please call " + PHONE); }
 finally { setSending(false); }
 };
 return (
 <>
 <PageHero eyebrow="Get in Touch" title="Contact RPM Auto Repair"
 subtitle="Questions, estimates, or ready to book? We're here to help, honestly and without the runaround."
 image="https://images.unsplash.com/photo-1676018366904-c083ed678e60?w=1400&q=80"
 crumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]} />
 <Section>
 <Container className="grid gap-12 lg:grid-cols-2">
 <div>
 <h2 className="font-display text-3xl text-white">Reach Us Directly</h2>
 <p className="mt-3 text-muted-foreground">The fastest way to reach us is by phone. We answer questions honestly and can usually get you scheduled the same week.</p>
 <div className="mt-8 space-y-4">
 {[
 { icon: Phone, label: "Call Us", value: PHONE, href: `tel:${PHONE_RAW}` },
 { icon: Mail, label: "Email Us", value: "rpmauto2011@gmail.com", href: "mailto:rpmauto2011@gmail.com" },
 { icon: MapPin, label: "Visit Us", value: "2650 E Alessandro Blvd, Riverside, CA 92508 (right off the 215 Fwy)" },
 { icon: Clock, label: "Hours", value: "Mon-Fri 8-6 · Sat 8-2 · Sun Closed" },
 ].map(({ icon: Icon, label, value, href }) => (
 <div key={label} className="flex items-start gap-4 rounded-xl border border-white/10 bg-steel p-5">
 <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/15 text-primary"><Icon size={20} /></div>
 <div>
 <div className="text-xs tracking-wide text-muted-foreground">{label}</div>
 {href ? <a href={href} className="font-semibold text-white hover:text-primary">{value}</a> : <div className="font-semibold text-white">{value}</div>}
 </div>
 </div>
 ))}
 </div>
 <div className="mt-6"><MapEmbed /></div>
 </div>
 <form onSubmit={onSubmit} className="space-y-5 rounded-2xl border border-white/10 bg-steel p-7 lg:sticky lg:top-24 lg:self-start" data-testid="contact-form">
 <h2 className="font-display text-2xl text-white">Send a Message</h2>
 <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Name *</Label><Input required value={form.name} onChange={set("name")} data-testid="contact-name" className="border-white/15 bg-ink text-white" /></div>
 <div className="grid gap-5 sm:grid-cols-2">
 <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Phone *</Label><Input required value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="border-white/15 bg-ink text-white" /></div>
 <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Email *</Label><Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="border-white/15 bg-ink text-white" /></div>
 </div>
 <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Message</Label><Textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="border-white/15 bg-ink text-white" /></div>
 <button type="submit" disabled={sending} data-testid="contact-submit" className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-4 font-semibold tracking-wide text-white hover:bg-racing-dark disabled:opacity-60">
 {sending ? "Sending…" : <>Send Message <ArrowRight size={16} /></>}
 </button>
 </form>
 </Container>
 </Section>
 <CtaBanner />
 </>
 );
}
